'use client';

import { useCallback } from 'react';
import useSWR, { mutate as globalMutate, type SWRConfiguration } from 'swr';
import useSWRMutation from 'swr/mutation';
import { useTranslation } from '@/lib/i18n';

const WITHDRAW_APPLY_PATH = '/live-trading/withdraw/apply';
const WITHDRAW_CONFIG_PATH = '/live-trading/withdraw/config';

export type WithdrawTier = 'auto' | 'manual';
export type WithdrawProgressStep =
  | 'submitted'
  | 'reviewing'
  | 'signing'
  | 'sending'
  | 'done'
  | 'rejected'
  | 'canceled';
export type WithdrawReviewReason =
  | 'amount_threshold'
  | 'daily_auto_limit'
  | 'signature_preparation_failed';

/** 提现申请状态：0=申请已记录,1=提现处理中,2=提现成功,3=提现失败,4=提现取消,5=审核拒绝 */
export enum WithdrawStatus {
  Recorded = 0,
  Processing = 1,
  Success = 2,
  Failed = 3,
  Cancelled = 4,
  Rejected = 5,
}

/** 提现申请记录（对应 withdraw_apply 表行，字段为路由 select 的 camelCase 键） */
export interface WithdrawRecord {
  id: number;
  userId: number;
  uid: string;
  /** decimal 字符串；-1 表示全部提现 */
  amount: string;
  chainId: number;
  address: string;
  status: WithdrawStatus;
  cancellable?: boolean;
  canceled_at?: string | null;
  tier?: WithdrawTier;
  step?: WithdrawProgressStep;
  signatures_done?: number;
  signatures_required?: number | null;
  reject_reason?: string | null;
  review_reason?: WithdrawReviewReason | null;
  /** 失败/拒绝原因（由后端 remark 解析而来），仅失败/拒绝状态非空，其余为 '' */
  message: string;
  createdAt: string;
  updatedAt: string;
}

export interface WithdrawPagination {
  page: number;
  page_size: number;
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export interface WithdrawRecordsResponse {
  success: boolean;
  data: WithdrawRecord[];
  pagination: WithdrawPagination;
}

export interface UseWithdrawRecordsOptions {
  limit?: number;
  offset?: number;
  /** 按状态筛选，支持多个（?status=3,4,5）；缺省查全部 */
  status?: WithdrawStatus | WithdrawStatus[];
  enabled?: boolean;
  swr?: SWRConfiguration<WithdrawRecordsResponse>;
}

/**
 * 查询提现申请记录（分页 limit/offset，可选 status 筛选）。
 * GET /api/live-trading/withdraw/apply
 */
export function useWithdrawRecords(options: UseWithdrawRecordsOptions = {}) {
  const { limit = 10, offset = 0, status, enabled = true, swr } = options;

  const searchParams = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const statusKey = serializeStatus(status);
  if (statusKey) searchParams.set('status', statusKey);

  const { data, error, isLoading, isValidating, mutate } = useSWR<WithdrawRecordsResponse>(
    enabled ? `api:${WITHDRAW_APPLY_PATH}?${searchParams.toString()}` : null,
    { revalidateOnFocus: false, ...swr },
  );

  return {
    records: data?.data ?? [],
    pagination: data?.pagination ?? null,
    total: data?.pagination?.total ?? 0,
    hasMore: data?.pagination?.has_more ?? false,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function serializeStatus(status?: WithdrawStatus | WithdrawStatus[]) {
  if (status === undefined) return '';
  const statuses = Array.isArray(status) ? status : [status];
  return statuses.join(',');
}

/** 作废全部提现记录列表缓存（各分页/筛选 key），申请提现后调用 */
export function invalidateWithdrawRecords() {
  return globalMutate(
    (key) => typeof key === 'string' && key.startsWith(`api:${WITHDRAW_APPLY_PATH}`),
  );
}

export interface ApplyWithdrawParams {
  /** 提现金额 > 0 */
  amount: number;
  /** 提现目标链 id（正整数），如 137=Polygon */
  chainId: number;
  /** 收款地址 */
  address: string;
}

export interface ApplyWithdrawResponse {
  success: boolean;
  code: 'DAILY_AUTO_LIMIT' | 'MANUAL_REVIEW_REQUIRED' | null;
  data: {
    id: number | null;
    uid: string;
    amount: number;
    chain_id: number;
    address: string;
    status: WithdrawStatus;
    tier: WithdrawTier;
    step: WithdrawProgressStep;
    signatures_done: number;
    signatures_required: number | null;
    reject_reason: string | null;
    review_reason: WithdrawReviewReason | null;
    decision_code: 'DAILY_AUTO_LIMIT' | 'MANUAL_REVIEW_REQUIRED' | null;
    daily_auto_used_usd: number | null;
    daily_auto_limit_usd: number;
  };
}

export interface WithdrawConfig {
  minimum_amount_usd: number;
  threshold_usd: number;
  daily_auto_limit_usd: number;
  auto_hours: number;
  manual_hours: number;
  multisig_signers_required: number | null;
  safe_single_address: string;
  safe_multi_address: string;
  quota_timezone: 'UTC';
}

export function useWithdrawConfig() {
  const { data, error, isLoading, mutate } = useSWR<WithdrawConfig>(`api:${WITHDRAW_CONFIG_PATH}`, {
    revalidateOnFocus: false,
  });

  return { config: data ?? null, error, isLoading, refresh: mutate };
}

/**
 * 申请提现。POST /api/live-trading/withdraw/apply
 * body: { amount, chain_id, address }；成功（201）后作废提现记录列表与余额缓存。
 */
export function useApplyWithdraw() {
  const { t } = useTranslation();
  const { trigger, isMutating, error } = useSWRMutation<ApplyWithdrawResponse>(
    `api:${WITHDRAW_APPLY_PATH}`,
  );

  const apply = useCallback(
    async ({ amount, chainId, address }: ApplyWithdrawParams) => {
      if (amount <= 0) {
        throw new Error(t('Amount must be greater than 0'));
      }
      if (!Number.isInteger(chainId) || chainId <= 0) {
        throw new Error(t('Invalid chain'));
      }
      if (!address.trim()) {
        throw new Error(t('Recipient address is required'));
      }
      const res = await trigger({
        method: 'POST',
        body: JSON.stringify({ amount, chain_id: chainId, address: address.trim() }),
      });
      await Promise.all([invalidateWithdrawRecords(), globalMutate('api:/live-trading/balance')]);
      return res;
    },
    [trigger, t],
  );

  return { apply, isApplying: isMutating, error };
}

export default useWithdrawRecords;
